import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { getApiBaseUrl } from '../../../../shared/config/runtime-config';
import { ApiResponse } from '../../../../shared/dto/response/api-response.dto';
import { buildHttpParams } from '../../../../shared/utils/http.utils';
import {
  AccreditationConfirmRequest,
  AccreditationConfirmResponse,
  AccreditationCreateGuestRequest,
  AccreditationCreateGuestResponse,
  AccreditationFindEventsRequest,
  AccreditationFindEventsResponse,
  AccreditationFindGuestRequest,
  AccreditationFindGuestResponse,
  AccreditationGuestPhotoRequest,
  AccreditationGuestPhotoResponse,
  AccreditationRefuseRequest,
  AccreditationRefuseResponse,
  AccreditationVerifyRequest,
  AccreditationVerifyResponse,
} from '../../domain/dto';
import { AccreditationRepository } from '../../domain/repository';

@Injectable({ providedIn: 'root' })
export class AccreditationRepositoryImpl implements AccreditationRepository {
  constructor(private readonly http: HttpClient) {}

  findEvents(
    request: AccreditationFindEventsRequest,
  ): Observable<HttpResponse<AccreditationFindEventsResponse>> {
    return this.http
      .get<ApiResponse<AccreditationFindEventsResponse>>(`${this.apiUrl}/events`, {
        observe: 'response',
        params: buildHttpParams(request),
      })
      .pipe(map((response) => response.clone({ body: response.body?.data })));
  }

  findGuest(
    request: AccreditationFindGuestRequest,
  ): Observable<HttpResponse<AccreditationFindGuestResponse>> {
    const { eventId, eventVenueId, eventVenueDateId, identification } = request;
    const encodedIdentification = encodeURIComponent(identification);

    return this.http
      .get<
        ApiResponse<AccreditationFindGuestResponse>
      >(`${this.apiUrl}/events/${eventId}/${eventVenueId}/${eventVenueDateId}/${encodedIdentification}`, { observe: 'response' })
      .pipe(map((response) => response.clone({ body: response.body?.data })));
  }

  createGuest(
    request: AccreditationCreateGuestRequest,
  ): Observable<HttpResponse<AccreditationCreateGuestResponse>> {
    const { eventId, ...payload } = request;
    return this.http
      .post<
        ApiResponse<AccreditationCreateGuestResponse>
      >(`${this.apiUrl}/events/${eventId}/guests`, payload, { observe: 'response' })
      .pipe(map((response) => response.clone({ body: response.body?.data })));
  }

  verify(
    request: AccreditationVerifyRequest,
  ): Observable<HttpResponse<AccreditationVerifyResponse>> {
    const { fingerprint, idempotencyKey } = request;
    return this.http
      .post<
        ApiResponse<AccreditationVerifyResponse>
      >(`${this.apiUrl}/verify`, { fingerprint }, { observe: 'response', headers: this.idempotencyHeaders(idempotencyKey) })
      .pipe(map((response) => response.clone({ body: response.body?.data })));
  }

  getGuestPhoto(
    request: AccreditationGuestPhotoRequest,
  ): Observable<AccreditationGuestPhotoResponse> {
    const { guestId, preview } = request;
    return this.http.get(`${this.photosApiUrl}/guests/${guestId}`, {
      params: buildHttpParams({ preview }),
      responseType: 'blob',
    });
  }

  confirm(
    request: AccreditationConfirmRequest,
  ): Observable<HttpResponse<AccreditationConfirmResponse>> {
    const { idempotencyKey, ...payload } = request;
    return this.http
      .post<ApiResponse<AccreditationConfirmResponse>>(`${this.apiUrl}/confirm`, payload, {
        observe: 'response',
        headers: this.idempotencyHeaders(idempotencyKey),
      })
      .pipe(map((response) => response.clone({ body: response.body?.data })));
  }

  refuse(
    request: AccreditationRefuseRequest,
  ): Observable<HttpResponse<AccreditationRefuseResponse>> {
    const { idempotencyKey, ...payload } = request;
    return this.http
      .post<ApiResponse<AccreditationRefuseResponse>>(`${this.apiUrl}/refuse`, payload, {
        observe: 'response',
        headers: this.idempotencyHeaders(idempotencyKey),
      })
      .pipe(map((response) => response.clone({ body: response.body?.data ?? {} })));
  }

  private idempotencyHeaders(idempotencyKey: string): HttpHeaders {
    return new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
  }

  private get apiUrl(): string {
    return getApiBaseUrl('accreditations');
  }

  private get photosApiUrl(): string {
    return getApiBaseUrl('photos');
  }
}
